import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Zap, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle, Clock,
  Euro, Wallet, Receipt, CreditCard, ArrowUpRight, ArrowDownRight, RefreshCw,
  ChevronRight, FileSignature, Calendar, Car, Users, AlertTriangle,
  MessageSquare, Sparkles, BarChart3, Send, Bot, User, Loader2, X,
  GripVertical, Settings, Mail, Eye, EyeOff, Save, History, Trash2, ChevronLeft,
  CalendarDays
} from 'lucide-react';
import ChatMessage from '../components/ChatMessage';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart, Cell, ReferenceLine
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
};

// Variation badge component
const VariationBadge = ({ value, inverted = false }) => {
  if (value === undefined || value === null) return null;
  
  const isPositive = inverted ? value < 0 : value > 0;
  const isNeutral = Math.abs(value) < 1;
  
  if (isNeutral) {
    return (
      <div className="flex items-center gap-1 text-slate-500 text-sm">
        <Minus className="w-3 h-3" />
        <span>Stable</span>
      </div>
    );
  }
  
  return (
    <div className={`flex items-center gap-1 text-sm font-medium ${
      isPositive ? 'text-green-600' : 'text-red-600'
    }`}>
      {isPositive ? (
        <ArrowUpRight className="w-4 h-4" />
      ) : (
        <ArrowDownRight className="w-4 h-4" />
      )}
      <span>{value > 0 ? '+' : ''}{value.toFixed(1)}%</span>
      <span className="text-xs text-slate-400 font-normal">vs mois préc.</span>
    </div>
  );
};

// Health Score Circle Component
const HealthScoreCircle = ({ score, status, color }) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const colorClasses = {
    green: { ring: 'stroke-green-500', bg: 'bg-green-50', text: 'text-green-700', badge: 'bg-green-100 text-green-800' },
    orange: { ring: 'stroke-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
    red: { ring: 'stroke-red-500', bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100 text-red-800' }
  };
  
  const classes = colorClasses[color] || colorClasses.orange;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="64" cy="64" r="45" fill="none" stroke="#e2e8f0" strokeWidth="8" />
          <circle
            cx="64" cy="64" r="45" fill="none"
            className={classes.ring}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${classes.text}`}>{score}</span>
          <span className="text-xs text-slate-500">/100</span>
        </div>
      </div>
      <Badge className={`mt-2 ${classes.badge}`}>{status}</Badge>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, variation, icon: Icon, color, subtitle, onClick, invertVariation = false, isDragging }) => {
  const colorClasses = {
    green: 'from-green-50 to-green-100 border-green-200',
    orange: 'from-orange-50 to-orange-100 border-orange-200',
    blue: 'from-blue-50 to-blue-100 border-blue-200',
    red: 'from-red-50 to-red-100 border-red-200',
    slate: 'from-slate-50 to-slate-100 border-slate-200'
  };
  
  const iconColors = {
    green: 'text-green-600 bg-green-100',
    orange: 'text-orange-600 bg-orange-100',
    blue: 'text-blue-600 bg-blue-100',
    red: 'text-red-600 bg-red-100',
    slate: 'text-slate-600 bg-slate-100'
  };
  
  return (
    <Card 
      className={`bg-gradient-to-br ${colorClasses[color]} cursor-pointer hover:shadow-md transition-all ${isDragging ? 'shadow-xl ring-2 ring-amber-400' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className={`p-2 rounded-lg ${iconColors[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
          {variation !== undefined && (
            <VariationBadge value={variation} inverted={invertVariation} />
          )}
        </div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{title}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

// Alert Item Component
const AlertItem = ({ alert, onNavigate }) => {
  const levelConfig = {
    urgent: { bg: 'bg-red-50 hover:bg-red-100', border: 'border-red-200', dot: 'bg-red-500' },
    attention: { bg: 'bg-orange-50 hover:bg-orange-100', border: 'border-orange-200', dot: 'bg-orange-500' },
    info: { bg: 'bg-green-50 hover:bg-green-100', border: 'border-green-200', dot: 'bg-green-500' }
  };
  
  const iconMap = {
    'alert-circle': AlertCircle,
    'trending-down': TrendingDown,
    'trending-up': TrendingUp,
    'calendar': Calendar,
    'calendar-days': Calendar,
    'file-signature': FileSignature,
    'car': Car,
    'check-circle': CheckCircle
  };
  
  const config = levelConfig[alert.level] || levelConfig.attention;
  const IconComponent = iconMap[alert.icon] || AlertCircle;
  
  return (
    <button
      onClick={() => onNavigate(alert.link)}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${config.bg} ${config.border}`}
    >
      <div className={`w-2 h-2 rounded-full ${config.dot} shrink-0`} />
      <div className={`p-1.5 rounded-md ${alert.level === 'urgent' ? 'bg-red-100' : alert.level === 'attention' ? 'bg-orange-100' : 'bg-green-100'}`}>
        <IconComponent className={`w-4 h-4 ${alert.level === 'urgent' ? 'text-red-600' : alert.level === 'attention' ? 'text-orange-600' : 'text-green-600'}`} />
      </div>
      <div className="flex-1 text-left">
        <p className="font-medium text-slate-900 text-sm">{alert.title}</p>
        <p className="text-xs text-slate-500">{alert.description}</p>
      </div>
      <Badge variant="outline" className="shrink-0 text-xs">{alert.action}</Badge>
      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
    </button>
  );
};

// Score Detail Component - Version explicite
const ScoreDetail = ({ label, score, maxScore, detail, data, type }) => {
  const percentage = (score / maxScore) * 100;
  
  // Couleur basée sur le score
  const getScoreColor = () => {
    if (score >= 20) return { bar: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50' };
    if (score >= 10) return { bar: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50' };
    return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' };
  };
  
  const colors = getScoreColor();
  
  // Explication détaillée selon le type
  const getExplanation = () => {
    switch(type) {
      case 'tresorerie':
        if (!data) return null;
        return (
          <div className="text-xs text-slate-500 mt-1 space-y-0.5">
            <div>Solde actuel : <span className="font-medium text-slate-700">{formatCurrency(data.solde_actuel)}</span></div>
            <div>Projection 30j : <span className={`font-medium ${data.projection_30j >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(data.projection_30j)}</span></div>
          </div>
        );
      case 'encaissements':
        if (!data) return null;
        return (
          <div className="text-xs text-slate-500 mt-1 space-y-0.5">
            <div>Impayés total : <span className="font-medium text-slate-700">{formatCurrency(data.total)}</span> ({data.count} factures)</div>
            <div>Dont &gt;30j : <span className="font-medium text-red-600">{formatCurrency(data.montant_retard)}</span> ({data.retard_count} factures)</div>
          </div>
        );
      case 'ca':
        if (!data) return null;
        return (
          <div className="text-xs text-slate-500 mt-1 space-y-0.5">
            <div>CA ce mois : <span className="font-medium text-slate-700">{formatCurrency(data.ca_mois)}</span></div>
            <div>CA mois préc. : <span className="font-medium text-slate-700">{formatCurrency(data.ca_precedent)}</span></div>
            <div>Variation : <span className={`font-medium ${data.variation >= 0 ? 'text-green-600' : 'text-red-600'}`}>{data.variation >= 0 ? '+' : ''}{data.variation?.toFixed(1)}%</span></div>
          </div>
        );
      case 'depenses':
        if (!data) return null;
        return (
          <div className="text-xs text-slate-500 mt-1 space-y-0.5">
            <div>Dépenses : <span className="font-medium text-slate-700">{formatCurrency(data.depenses)}</span></div>
            <div>CA facturé : <span className="font-medium text-slate-700">{formatCurrency(data.ca_facture)}</span></div>
            <div>Ratio : <span className={`font-medium ${data.ratio < 50 ? 'text-green-600' : data.ratio < 80 ? 'text-orange-600' : 'text-red-600'}`}>{data.ratio?.toFixed(0)}%</span></div>
          </div>
        );
      default:
        return null;
    }
  };
  
  // Règles de notation
  const getRules = () => {
    switch(type) {
      case 'tresorerie':
        return "25 pts si positive et stable, 15 pts si positive mais en baisse, 5 pts si négative";
      case 'encaissements':
        return "25 pts si <5% d'impayés, 15 pts si 5-20%, 5 pts si >20%";
      case 'ca':
        return "25 pts si CA en hausse >5%, 15 pts si stable (±5%), 5 pts si baisse >5%";
      case 'depenses':
        return "25 pts si <50% du CA, 15 pts si 50-80%, 5 pts si >80%";
      default:
        return "";
    }
  };
  
  return (
    <div className={`p-3 rounded-lg ${colors.bg} border border-slate-200`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-semibold text-slate-800">{label}</span>
        <span className={`text-sm font-bold ${colors.text}`}>{score}/{maxScore} pts</span>
      </div>
      
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full ${colors.bar} transition-all duration-500`} 
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      <p className={`text-xs font-medium ${colors.text}`}>{detail}</p>
      
      {getExplanation()}
      
      <p className="text-xs text-slate-400 mt-2 italic border-t border-slate-200 pt-2">
        {getRules()}
      </p>
    </div>
  );
};

// Settings Dialog Component
const SettingsDialog = ({ open, onOpenChange }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [scheduledSends, setScheduledSends] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduling, setScheduling] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchScheduledSends();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/pilotage/report/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledSends = async () => {
    try {
      const res = await axios.get(`${API}/api/pilotage/report/scheduled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledSends(res.data.scheduled_sends || []);
    } catch (error) {
      console.error('Error fetching scheduled sends:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/pilotage/report/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/pilotage/report/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(`Rapport envoyé à ${res.data.sent} destinataire(s)`);
      } else {
        toast.error(res.data.error || 'Échec de l\'envoi');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const scheduleReport = async () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    
    setScheduling(true);
    try {
      const scheduledDateTime = `${scheduleDate}T${scheduleTime}:00`;
      const res = await axios.post(`${API}/api/pilotage/report/schedule`, {
        scheduled_date: scheduledDateTime,
        recipients: settings.recipients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      setScheduleDate('');
      setScheduleTime('09:00');
      await fetchScheduledSends();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la planification');
    } finally {
      setScheduling(false);
    }
  };

  const cancelScheduledSend = async (sendId) => {
    try {
      await axios.delete(`${API}/api/pilotage/report/scheduled/${sendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Envoi planifié annulé');
      await fetchScheduledSends();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const addRecipient = () => {
    if (newRecipient && !settings.recipients.includes(newRecipient)) {
      setSettings({ ...settings, recipients: [...settings.recipients, newRecipient] });
      setNewRecipient('');
    }
  };

  const removeRecipient = (email) => {
    setSettings({ ...settings, recipients: settings.recipients.filter(r => r !== email) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-amber-500" />
            Rapport par email
          </DialogTitle>
          <DialogDescription>
            Configurez l'envoi des rapports par email
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : settings && (
          <div className="space-y-6 py-4">
            {/* Email Sender Configuration */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuration expéditeur
              </h4>
              
              <div className="space-y-2">
                <Label htmlFor="sender-email" className="text-xs text-slate-500">Email expéditeur</Label>
                <Input
                  id="sender-email"
                  type="email"
                  placeholder="noreply@votredomaine.fr"
                  value={settings.sender_email || ''}
                  onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                  className="text-sm"
                />
                <p className="text-xs text-slate-400">
                  Laissez vide pour utiliser l'adresse par défaut (onboarding@resend.dev)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sender-name" className="text-xs text-slate-500">Nom expéditeur</Label>
                <Input
                  id="sender-name"
                  placeholder="BE-TAYE Rapports"
                  value={settings.sender_name || ''}
                  onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label>Destinataires</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@exemple.com"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                />
                <Button type="button" variant="outline" onClick={addRecipient}>
                  Ajouter
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {settings.recipients?.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="cursor-pointer hover:bg-red-100"
                    onClick={() => removeRecipient(email)}
                  >
                    {email}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>

            {/* Schedule Send */}
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-sm flex items-center gap-2 text-blue-800">
                <Clock className="w-4 h-4" />
                Planifier un envoi
              </h4>
              
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Date</Label>
                  <Input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="text-sm"
                  />
                </div>
                <div className="w-28">
                  <Label className="text-xs text-slate-500">Heure</Label>
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
              
              <Button 
                onClick={scheduleReport} 
                disabled={scheduling || !scheduleDate || !settings.recipients?.length}
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                {scheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                Planifier l'envoi
              </Button>
            </div>

            {/* Scheduled Sends List */}
            {scheduledSends.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Envois planifiés</Label>
                <div className="space-y-2">
                  {scheduledSends.map((send) => (
                    <div 
                      key={send.id} 
                      className={`flex items-center justify-between p-2 rounded-lg border text-sm ${
                        send.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                        send.status === 'sent' ? 'bg-green-50 border-green-200' :
                        'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {send.status === 'pending' ? (
                          <Clock className="w-4 h-4 text-amber-600" />
                        ) : send.status === 'sent' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                        <span>
                          {new Date(send.scheduled_date).toLocaleString('fr-FR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                            timeZone: 'Europe/Paris'
                          })}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {send.recipients?.length || 0} dest.
                        </Badge>
                      </div>
                      {send.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelScheduledSend(send.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Auto Report Settings */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center justify-between">
                <Label htmlFor="report-enabled" className="flex flex-col">
                  <span className="font-medium">Rapport automatique hebdomadaire</span>
                  <span className="text-xs text-slate-500">Envoi récurrent chaque semaine</span>
                </Label>
                <Switch
                  id="report-enabled"
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>
              
              {settings.enabled && (
                <div className="flex gap-2 mt-2">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">Jour</Label>
                    <select
                      value={settings.day || 'monday'}
                      onChange={(e) => setSettings({ ...settings, day: e.target.value })}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="monday">Lundi</option>
                      <option value="tuesday">Mardi</option>
                      <option value="wednesday">Mercredi</option>
                      <option value="thursday">Jeudi</option>
                      <option value="friday">Vendredi</option>
                    </select>
                  </div>
                  <div className="w-20">
                    <Label className="text-xs text-slate-500">Heure</Label>
                    <select
                      value={settings.hour || 7}
                      onChange={(e) => setSettings({ ...settings, hour: parseInt(e.target.value) })}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Last Sent */}
            {settings.last_sent && (
              <p className="text-xs text-slate-500">
                Dernier envoi : {new Date(settings.last_sent).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={saveSettings} disabled={saving} className="flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
              <Button 
                variant="outline" 
                onClick={sendNow} 
                disabled={sending || !settings.recipients?.length}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer maintenant
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Pilotage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [chartsData, setChartsData] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Period filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'year', 'global'
  
  // Available years for filter
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);
  
  // Month names
  const monthNames = [
    { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' }
  ];
  
  // Drag & drop state
  const [editMode, setEditMode] = useState(false);
  const [widgetLayout, setWidgetLayout] = useState([
    { id: 'kpi-ca', order: 0, visible: true },
    { id: 'kpi-tresorerie', order: 1, visible: true },
    { id: 'kpi-impayes', order: 2, visible: true },
    { id: 'kpi-depenses', order: 3, visible: true }
  ]);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  
  // Settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // AI Chatbot state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSuggestions, setChatSuggestions] = useState([]);
  const [aiInsight, setAiInsight] = useState('');
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const chatEndRef = useRef(null);
  
  const token = localStorage.getItem('token');

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      // Build query params based on view mode
      let params = '';
      if (viewMode === 'year') {
        params = `?year=${selectedYear}`;
      } else if (viewMode === 'month') {
        params = `?year=${selectedYear}&month=${selectedMonth}`;
      }
      // 'global' mode: no params (uses current month)
      
      const res = await axios.get(`${API}/api/pilotage/summary${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching pilotage data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, viewMode, selectedYear, selectedMonth]);

  const fetchCharts = useCallback(async () => {
    try {
      // Build query params based on view mode
      let params = '';
      if (viewMode === 'year') {
        params = `?year=${selectedYear}`;
      } else if (viewMode === 'month') {
        params = `?year=${selectedYear}&month=${selectedMonth}`;
      }
      
      const res = await axios.get(`${API}/api/pilotage/charts/all${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartsData(res.data);
    } catch (error) {
      console.error('Error fetching charts:', error);
    } finally {
      setChartsLoading(false);
    }
  }, [token, viewMode, selectedYear, selectedMonth]);

  const fetchInsight = useCallback(async () => {
    try {
      // Build query params based on view mode
      let params = '';
      if (viewMode === 'year') {
        params = `?year=${selectedYear}`;
      } else if (viewMode === 'month') {
        params = `?year=${selectedYear}&month=${selectedMonth}`;
      }
      
      const res = await axios.get(`${API}/api/pilotage/ai/insight${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiInsight(res.data.insight);
    } catch (error) {
      console.error('Error fetching insight:', error);
    }
  }, [token, viewMode, selectedYear, selectedMonth]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/pilotage/ai/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatSuggestions(res.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  }, [token]);

  // Fetch chat history
  const fetchChatHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/pilotage/ai/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatSessions(res.data.sessions || []);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  }, [token]);

  // Save current session
  const saveChatSession = useCallback(async (messages, sessionId) => {
    if (messages.length === 0) return;
    
    // Generate title from first user message
    const firstUserMsg = messages.find(m => m.role === 'user');
    const title = firstUserMsg ? firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? '...' : '') : 'Conversation';
    
    try {
      await axios.post(
        `${API}/api/pilotage/ai/history?session_id=${sessionId}&title=${encodeURIComponent(title)}`,
        messages,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh history
      fetchChatHistory();
    } catch (error) {
      console.error('Error saving chat session:', error);
    }
  }, [token, fetchChatHistory]);

  // Load a specific session
  const loadChatSession = async (session) => {
    setChatMessages(session.messages || []);
    setCurrentSessionId(session.session_id);
    setShowHistory(false);
  };

  // Delete a session
  const deleteChatSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/pilotage/ai/history/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChatHistory();
      if (currentSessionId === sessionId) {
        setChatMessages([]);
        setCurrentSessionId(null);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const fetchLayout = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/pilotage/layout`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.layout) {
        setWidgetLayout(res.data.layout);
      }
    } catch (error) {
      console.error('Error fetching layout:', error);
    } finally {
      setLayoutLoaded(true);
    }
  }, [token]);

  const saveLayout = async (newLayout) => {
    try {
      await axios.put(`${API}/api/pilotage/layout`, { layout: newLayout }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving layout:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCharts();
    fetchInsight();
    fetchSuggestions();
    fetchLayout();
    fetchChatHistory();
  }, [fetchData, fetchCharts, fetchInsight, fetchSuggestions, fetchLayout, fetchChatHistory]);

  // Handle drag end
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(widgetLayout);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order values
    const updatedItems = items.map((item, index) => ({ ...item, order: index }));
    setWidgetLayout(updatedItems);
    saveLayout(updatedItems);
  };

  const toggleWidgetVisibility = (widgetId) => {
    const updatedLayout = widgetLayout.map(w => 
      w.id === widgetId ? { ...w, visible: !w.visible } : w
    );
    setWidgetLayout(updatedLayout);
    saveLayout(updatedLayout);
  };

  // Send chat message
  const sendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatLoading(true);
    
    // Generate session ID if new conversation
    const sessionId = currentSessionId || `session_${Date.now()}`;
    if (!currentSessionId) {
      setCurrentSessionId(sessionId);
    }
    
    const newMessages = [...chatMessages, { role: 'user', content: userMessage }];
    setChatMessages(newMessages);
    
    try {
      const res = await axios.post(
        `${API}/api/pilotage/ai/chat`,
        { messages: chatMessages, user_message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const finalMessages = [...newMessages, { role: 'assistant', content: res.data.response }];
      setChatMessages(finalMessages);
      // Auto-save session
      saveChatSession(finalMessages, sessionId);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatMessages([...newMessages, { role: 'assistant', content: 'Désolé, une erreur s\'est produite.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const resetChat = () => {
    setChatMessages([]);
    setChatInput('');
    setCurrentSessionId(null);
  };

  const handleRefresh = () => fetchData(true);
  const handleNavigate = (path) => navigate(path);

  // Loading state
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  const { health, kpis, alerts } = data || {};

  // KPI widget render function
  const renderKPIWidget = (widgetId, isDragging = false) => {
    const isYearView = viewMode === 'year';
    const periodLabel = isYearView 
      ? `Année ${selectedYear}` 
      : `${monthNames.find(m => m.value === selectedMonth)?.label} ${selectedYear}`;
    
    const kpiConfig = {
      'kpi-ca': {
        title: isYearView ? "CA annuel" : "CA du mois",
        value: formatCurrency(kpis?.ca_mois?.value),
        variation: kpis?.ca_mois?.variation,
        icon: Euro,
        color: "green",
        subtitle: periodLabel,
        onClick: () => handleNavigate('/analyse')
      },
      'kpi-tresorerie': {
        title: "Trésorerie",
        value: formatCurrency(kpis?.tresorerie?.value),
        variation: kpis?.tresorerie?.variation,
        icon: Wallet,
        color: "blue",
        subtitle: "Solde bancaire actuel",
        onClick: () => handleNavigate('/rapprochement')
      },
      'kpi-impayes': {
        title: "Impayés",
        value: formatCurrency(kpis?.impayes?.value),
        icon: Receipt,
        color: "red",
        subtitle: `${kpis?.impayes?.count || 0} facture(s) en attente`,
        onClick: () => handleNavigate('/factures?filter=impaye')
      },
      'kpi-depenses': {
        title: isYearView ? "Dépenses annuelles" : "Dépenses",
        value: formatCurrency(kpis?.depenses?.value),
        variation: kpis?.depenses?.variation,
        invertVariation: true,
        icon: CreditCard,
        color: "orange",
        subtitle: periodLabel,
        onClick: () => handleNavigate('/achats')
      }
    };

    const config = kpiConfig[widgetId];
    if (!config) return null;

    return (
      <KPICard
        {...config}
        isDragging={isDragging}
      />
    );
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-white min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            Pilotage
          </h1>
          <p className="text-slate-500 text-sm">
            Vue d'ensemble de la santé de votre entreprise
          </p>
        </div>
        
        {/* Period Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* View Mode Selector */}
          <div className="flex items-center gap-2 bg-white border rounded-lg p-1">
            <Button
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('month')}
              className={viewMode === 'month' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
            >
              Mois
            </Button>
            <Button
              variant={viewMode === 'year' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('year')}
              className={viewMode === 'year' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
            >
              Année
            </Button>
          </div>
          
          {/* Year Selector */}
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] bg-white">
              <CalendarDays className="w-4 h-4 mr-1 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableYears.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Month Selector (only in month mode) */}
          {viewMode === 'month' && (
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[130px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-2 ml-2">
            {lastUpdated && (
              <span className="text-xs text-slate-400 hidden sm:inline">
                {lastUpdated.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })}
              </span>
            )}
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={() => setEditMode(!editMode)}
              className={editMode ? "bg-amber-500 hover:bg-amber-600" : ""}
            >
              <GripVertical className="w-4 h-4 mr-1" />
              {editMode ? "Terminer" : "Personnaliser"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              <Mail className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Period Info Banner */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-amber-600" />
        <span className="text-sm text-amber-800 font-medium">
          {viewMode === 'year' 
            ? `Données de l'année ${selectedYear}` 
            : `Données de ${monthNames.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
          }
        </span>
        {data?.view_mode && (
          <Badge variant="outline" className="ml-auto text-xs border-amber-300 text-amber-700">
            {data.view_mode === 'year' ? 'Vue annuelle' : 'Vue mensuelle'}
          </Badge>
        )}
      </div>

      {/* Edit Mode Banner */}
      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
          <GripVertical className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            <strong>Mode personnalisation</strong> — Glissez-déposez les KPIs pour les réorganiser. 
            Cliquez sur l'icône <EyeOff className="w-3 h-3 inline" /> pour masquer un widget.
          </p>
        </div>
      )}

      {/* Score de santé + Alertes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score de santé */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Score de santé
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center pt-2">
            {health && (
              <>
                <HealthScoreCircle score={health.score} status={health.status} color={health.color} />
                
                {aiInsight && (
                  <div className="w-full mt-4 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-900 leading-relaxed">{aiInsight}</p>
                    </div>
                  </div>
                )}
                
                <div className="w-full mt-4 space-y-3">
                  <ScoreDetail 
                    label="Trésorerie" 
                    score={health.components?.tresorerie || 0} 
                    maxScore={25} 
                    detail={health.details?.tresorerie}
                    type="tresorerie"
                    data={health.data?.tresorerie}
                  />
                  <ScoreDetail 
                    label="Encaissements" 
                    score={health.components?.encaissements || 0} 
                    maxScore={25} 
                    detail={health.details?.encaissements}
                    type="encaissements"
                    data={health.data?.impayes}
                  />
                  <ScoreDetail 
                    label="CA" 
                    score={health.components?.ca || 0} 
                    maxScore={25} 
                    detail={health.details?.ca}
                    type="ca"
                    data={{
                      ca_mois: health.data?.ca_mois?.facture,
                      ca_precedent: health.data?.ca_precedent?.facture,
                      variation: health.data?.variation_ca
                    }}
                  />
                  <ScoreDetail 
                    label="Dépenses" 
                    score={health.components?.depenses || 0} 
                    maxScore={25} 
                    detail={health.details?.depenses}
                    type="depenses"
                    data={{
                      depenses: health.data?.depenses_mois,
                      ca_facture: health.data?.ca_mois?.facture,
                      ratio: health.data?.ca_mois?.facture > 0 
                        ? (health.data?.depenses_mois / health.data?.ca_mois?.facture) * 100 
                        : 0
                    }}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Alertes proactives */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Alertes proactives
                </CardTitle>
                <CardDescription>Actions prioritaires</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts && alerts.length > 0 ? (
              alerts.slice(0, 5).map(alert => (
                <AlertItem key={alert.id} alert={alert} onNavigate={handleNavigate} />
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-green-700 font-medium">Aucune alerte</p>
                <p className="text-sm text-slate-500">Tout est sous contrôle</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPIs Cards with Drag & Drop */}
      {layoutLoaded && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="kpis" direction="horizontal">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="grid grid-cols-2 lg:grid-cols-4 gap-4"
              >
                {widgetLayout
                  .filter(w => w.visible || editMode)
                  .sort((a, b) => a.order - b.order)
                  .map((widget, index) => (
                    <Draggable
                      key={widget.id}
                      draggableId={widget.id}
                      index={index}
                      isDragDisabled={!editMode}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`relative ${!widget.visible ? 'opacity-50' : ''}`}
                        >
                          {editMode && (
                            <div className="absolute -top-2 -right-2 z-10 flex gap-1">
                              <button
                                {...provided.dragHandleProps}
                                className="p-1 bg-amber-500 text-white rounded-full shadow-md hover:bg-amber-600"
                              >
                                <GripVertical className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => toggleWidgetVisibility(widget.id)}
                                className={`p-1 rounded-full shadow-md ${widget.visible ? 'bg-slate-500 text-white' : 'bg-green-500 text-white'}`}
                              >
                                {widget.visible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                            </div>
                          )}
                          {renderKPIWidget(widget.id, snapshot.isDragging)}
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Graphique CA 12 mois */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-500" />
              CA — 12 derniers mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartsLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : chartsData?.ca_12_months?.data ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartsData.ca_12_months.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                  <Bar dataKey="facture" name="Facturé" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={35} />
                  <Bar dataKey="encaisse" name="Encaissé" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={35} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">Aucune donnée</div>
            )}
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-500" /><span className="text-xs text-slate-600">Facturé</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-500" /><span className="text-xs text-slate-600">Encaissé</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Top 5 Clients */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-500" />
              Top 5 Clients
            </CardTitle>
            <CardDescription>{chartsData?.top_clients?.quarter || 'Ce trimestre'}</CardDescription>
          </CardHeader>
          <CardContent>
            {chartsLoading ? (
              <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10" />)}</div>
            ) : chartsData?.top_clients?.data?.length > 0 ? (
              <div className="space-y-3">
                {chartsData.top_clients.data.map((client, idx) => (
                  <button key={idx} onClick={() => handleNavigate(`/clients?search=${encodeURIComponent(client.client)}`)} className="w-full text-left group">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 truncate max-w-[160px]">{idx + 1}. {client.client}</span>
                      <span className="text-sm font-bold text-slate-900">{formatCurrency(client.total)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{ width: `${client.percentage}%` }} />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">Aucune donnée</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Graphique Trésorerie Prévisionnelle */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-500" />
                Trésorerie prévisionnelle — 60 jours
              </CardTitle>
            </div>
            {chartsData?.tresorerie_60j?.negative_day && (
              <Badge variant="destructive" className="animate-pulse">
                Passage en négatif dans {chartsData.tresorerie_60j.negative_day} jours
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {chartsLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : chartsData?.tresorerie_60j?.data ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartsData.tresorerie_60j.data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSolde" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Solde']} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                  <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2} />
                  <Area type="monotone" dataKey="solde" stroke="#10b981" strokeWidth={2} fill="url(#colorSolde)" />
                </AreaChart>
              </ResponsiveContainer>
              
              <div className="flex justify-center gap-8 mt-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Solde actuel</p>
                  <p className="text-lg font-bold text-slate-900">{formatCurrency(chartsData.tresorerie_60j.solde_actuel)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Encaissements attendus</p>
                  <p className="text-lg font-bold text-green-600">+{formatCurrency(chartsData.tresorerie_60j.encaissements_attendus)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Dépenses prévues</p>
                  <p className="text-lg font-bold text-red-600">-{formatCurrency(chartsData.tresorerie_60j.depenses_prevues)}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">Aucune donnée</div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button variant="outline" onClick={() => handleNavigate('/factures/nouvelle')}>
              <Receipt className="w-4 h-4 mr-2" />Nouvelle facture
            </Button>
            <Button variant="outline" onClick={() => handleNavigate('/factures?filter=impaye')}>
              <AlertCircle className="w-4 h-4 mr-2" />Relancer les impayés
            </Button>
            <Button variant="outline" onClick={() => handleNavigate('/rapprochement')}>
              <Wallet className="w-4 h-4 mr-2" />Rapprochement
            </Button>
            <Button variant="outline" onClick={() => handleNavigate('/contrats')}>
              <FileSignature className="w-4 h-4 mr-2" />Contrats
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      {/* Floating Chat Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50 hover:scale-105"
        data-testid="chat-toggle"
      >
        {chatOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      {/* Chat Panel */}
      {chatOpen && (
        <div className="fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-96 max-w-[400px] h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-3 py-2.5 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {showHistory && (
                <button onClick={() => setShowHistory(false)} className="p-1.5 hover:bg-white/20 rounded shrink-0" title="Retour">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <Bot className="w-5 h-5 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-sm truncate">{showHistory ? 'Historique' : 'BE-TAYE IA'}</h3>
                <p className="text-[10px] sm:text-xs text-amber-100 truncate">
                  {showHistory ? `${chatSessions.length} conversation${chatSessions.length > 1 ? 's' : ''}` : 'Assistant (lecture seule)'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button 
                onClick={() => setShowHistory(!showHistory)} 
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Historique des conversations"
              >
                <History className="w-4 h-4" />
              </button>
              <button 
                onClick={resetChat} 
                className="p-2 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-1"
                title="Nouvelle conversation"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Nouvelle</span>
              </button>
            </div>
          </div>

          {showHistory ? (
            <div className="flex-1 overflow-y-auto p-3">
              {chatSessions.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune conversation enregistrée</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {chatSessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="flex items-center bg-slate-50 hover:bg-amber-50 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors"
                    >
                      {/* Zone cliquable */}
                      <div 
                        className="flex-1 min-w-0 p-2.5 cursor-pointer"
                        onClick={() => loadChatSession(session)}
                      >
                        <p className="text-sm font-medium text-slate-700 truncate">{session.title}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {new Date(session.updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })} • {session.messages?.length || 0} msg
                        </p>
                      </div>
                      {/* Bouton supprimer - toujours visible */}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteChatSession(session.session_id, e); }}
                        className="w-10 h-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-red-500 rounded-r-lg transition-all border-l border-slate-200 shrink-0"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <ScrollArea className="flex-1 p-4">
                {chatMessages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-4">
                      <Bot className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                      <p className="text-slate-600 font-medium">Bonjour ! Je suis BE-TAYE IA</p>
                      <p className="text-sm text-slate-400 mt-1">Posez-moi vos questions financières.</p>
                      <p className="text-xs text-slate-300 mt-2">Mode lecture seule - Je ne peux pas modifier vos données</p>
                    </div>
                    
                    {chatSessions.length > 0 && (
                      <div className="mb-4">
                        <button 
                          onClick={() => setShowHistory(true)}
                          className="w-full text-left p-2.5 text-sm bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-200 transition-colors flex items-center gap-2"
                        >
                          <History className="w-4 h-4 text-amber-600" />
                          <span className="text-amber-700">Voir mes {chatSessions.length} conversation(s) passée(s)</span>
                        </button>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">Suggestions</p>
                      {chatSuggestions.slice(0, 4).map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => setChatInput(suggestion)}
                          className="w-full text-left p-2.5 text-sm bg-slate-50 hover:bg-amber-50 rounded-lg border border-slate-200 hover:border-amber-300 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <ChatMessage 
                        key={idx}
                        message={msg.content}
                        isUser={msg.role === 'user'}
                      />
                    ))}
                    {chatLoading && (
                      <div className="flex gap-2 justify-start">
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-md">
                          <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </ScrollArea>

              <div className="border-t p-2.5 bg-slate-50 shrink-0">
                <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Posez votre question..."
                    className="flex-1 bg-white text-sm h-9"
                    disabled={chatLoading}
                  />
                  <Button 
                    type="submit" 
                    size="icon" 
                    disabled={!chatInput.trim() || chatLoading} 
                    className="bg-amber-500 hover:bg-amber-600 h-9 w-9 shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
